import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, object=auth
  const [error, setError] = useState("");

  useEffect(() => {
    // 1. Accept token from URL hash (used when opening web inside the mobile WebView).
    //    Format: #token=ACCESS_TOKEN — stored, then hash is wiped from the URL bar.
    try {
      if (typeof window !== "undefined" && window.location.hash.startsWith("#token=")) {
        const tok = decodeURIComponent(window.location.hash.slice(7));
        if (tok) {
          localStorage.setItem("access_token", tok);
          // Hide the token from the URL bar
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
        }
      }
    } catch {}

    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        // Bridge: tell native WebView wrapper about restored session so it can register Expo push token.
        try {
          if (typeof window !== "undefined" && window.ReactNativeWebView?.postMessage) {
            const tok = localStorage.getItem("access_token");
            if (tok) {
              window.ReactNativeWebView.postMessage(JSON.stringify({
                type: "AUTH",
                token: tok,
                userId: data?.id,
              }));
            }
          }
        } catch {}
      } catch {
        setUser(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      setUser(data.user);
      // Bridge: tell native WebView wrapper about the fresh token + user so it can register Expo push token.
      try {
        if (typeof window !== "undefined" && window.ReactNativeWebView?.postMessage) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: "AUTH",
            token: data.access_token,
            userId: data.user?.id,
          }));
        }
      } catch {}
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const loginWithSession = (data) => {
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data.user);
    try {
      if (typeof window !== "undefined" && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: "AUTH", token: data.access_token, userId: data.user?.id,
        }));
      }
    } catch {}
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("access_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithSession, logout, error, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
